import clsx from "clsx";
import { Tag } from "react-migration/components/Tag";
import { Button } from "react-migration/components/Button";
import { AddOn } from "../types";
import { EmbeddedYoutubeVideo } from "./EmbeddedYoutubeVideo";
import { AddOnPreviewImage } from "./AddOnPreviewImage";

const PREVIEW_ASSET_WIDTH = 368;
const PREVIEW_ASSET_HEIGHT = 284;

type AddOnPromotionPageProps = AddOn & {
  videoId?: string;
  videoTitle?: string;
  previewImages: {
    src: string;
    alt: string;
  }[];
  onPrimaryActionClick: () => void;
  primaryActionText: string;
  primaryActionDisabled?: boolean;
};

export const AddOnPromotionPage = ({
  title,
  subtitle,
  description,
  logoSrc,
  videoId,
  videoTitle,
  previewImages,
  tags,
  onPrimaryActionClick,
  primaryActionText,
  primaryActionDisabled = false,
}: AddOnPromotionPageProps) => (
  <article className={clsx("flex flex-col gap-6", "md:pb-[78px]")}>
    <div
      className={clsx(
        "flex w-full rounded-2xl border border-[rgba(23,25,25,0.08)] bg-background-surface overflow-hidden",
        "flex-col md:flex-row"
      )}
    >
      <div
        className={clsx(
          "flex flex-col justify-center items-center shrink-0 altas-grow-0 bg-blue-grey-50",
          "min-h-14 basis-14 md:min-h-[170px] md:basis-[170px] max-md:rounded-t-2xl md:rounded-l-2xl"
        )}
      >
        <img src={logoSrc} alt={`${title} Logo`} className="w-32" />
      </div>
      <section className="flex flex-col gap-2 p-6 grow">
        <div className="flex flex-col gap-2">
          <div className="pt-[3px]">
            <h1 className="inline text-2xl font-bold -tracking-wide">{title}</h1>
            {subtitle && <span className="ml-1 font-normal text-3xs text-platform-blue-700">{subtitle}</span>}
          </div>
          <div className={clsx("flex gap-2 justify-between", "flex-col md:flex-row")}>
            <p className="text-sm text-text-base-secondary-a">{description}</p>
            <Button
              className={clsx("shrink-0 grow-0 h-9 mb-3", "self-stretch md:self-start")}
              variant="primary"
              size="md"
              onClick={onPrimaryActionClick}
              disabled={primaryActionDisabled}
            >
              {primaryActionText}
            </Button>
          </div>
        </div>
        <div className="flex flex-row max-w-full gap-4 overflow-x-auto">
          {tags.map((tag, index) => (
            <Tag key={index} tag={tag.text} color={tag.color} size="xs" />
          ))}
        </div>
      </section>
    </div>
    <section className="flex flex-col gap-5">
      <h1 className="ml-4 text-2xl font-bold">Preview</h1>
      <div className={clsx("flex gap-6 w-full overflow-x-auto", "flex-col md:flex-row md:h-[284px]")}>
        {videoId && videoTitle && (
          <EmbeddedYoutubeVideo
            videoId={videoId}
            title={videoTitle}
            width={PREVIEW_ASSET_WIDTH}
            height={PREVIEW_ASSET_HEIGHT}
          />
        )}
        {previewImages.map((image, index) => (
          <AddOnPreviewImage
            key={index}
            src={image.src}
            alt={image.alt}
            width={PREVIEW_ASSET_WIDTH}
            height={PREVIEW_ASSET_HEIGHT}
          />
        ))}
      </div>
    </section>
  </article>
);
